import React, { useEffect, useMemo, useState } from 'react';
import { isValidMarcacaoId } from '../config/prospeccaoMarcacao';
import cccApi from '../api/cccApi';
import ComercialManagement from '@crm/components/Comercial/ComercialManagement';
import '@crm/components/Comercial/ComercialManagement.css';

type ClientSeed = {
  id: string;
  company: string;
  nif?: string;
};

type MarcacaoSeed = {
  id?: number;
  marcacao_id?: string;
  nome_empresa?: string;
  nome_responsavel?: string;
  morada?: string;
  contacto_cliente?: string;
  email?: string;
  empresa?: string;
  responsavel?: string;
  form_data?: Record<string, unknown>;
  [key: string]: unknown;
};

type Props = {
  client: ClientSeed;
  marcacaoId: string;
  onClose: () => void;
  onSuccess: () => void;
};

const swallowOverlayBackdropPointer = (e: React.MouseEvent) => {
  if (e.target === e.currentTarget) {
    e.stopPropagation();
  }
};

const CccSubmeterContratoRetencaoClientOverlay: React.FC<Props> = ({
  client,
  marcacaoId,
  onClose,
  onSuccess,
}) => {
  const [seed, setSeed] = useState<Record<string, MarcacaoSeed> | null>(null);
  const resolvedMarcacaoId = useMemo(
    () => (isValidMarcacaoId(marcacaoId) ? marcacaoId.trim() : ''),
    [marcacaoId]
  );

  const embeddedContractApi = useMemo(
    () => ({
      uploadFile: async (formData: FormData) => {
        try {
          return await cccApi.uploadRetencaoClientContractFile(client.id, formData);
        } catch (err: unknown) {
          const ax = err as { response?: { data?: { message?: string; error?: string } } };
          const msg =
            ax.response?.data?.message ||
            ax.response?.data?.error ||
            (err instanceof Error ? err.message : 'Falha ao enviar ficheiro');
          return { success: false, error: msg, message: msg };
        }
      },
      persist: async (contractData: Record<string, unknown>) => {
        try {
          const res = await cccApi.submitRetencaoClientContract(client.id, contractData);
          if (!res.success) {
            throw new Error(res.message || 'Erro ao salvar dados do contrato');
          }
        } catch (err: unknown) {
          const ax = err as { response?: { data?: { message?: string } } };
          throw new Error(
            ax.response?.data?.message ||
              (err instanceof Error ? err.message : 'Erro ao salvar dados do contrato')
          );
        }
      },
    }),
    [client.id]
  );

  useEffect(() => {
    if (!resolvedMarcacaoId) return;
    let cancelled = false;

    const fallbackSeed: Record<string, MarcacaoSeed> = {
      [resolvedMarcacaoId]: {
        marcacao_id: resolvedMarcacaoId,
        nome_empresa: client.company,
        empresa: client.company,
      },
    };

    const normalizeSeedEntry = (raw: MarcacaoSeed): MarcacaoSeed => ({
      ...raw,
      marcacao_id: raw.marcacao_id || resolvedMarcacaoId,
      empresa: raw.nome_empresa || raw.empresa || client.company,
      responsavel: raw.nome_responsavel || raw.responsavel,
    });

    (async () => {
      try {
        const data = await cccApi.getRetencaoClientMarcacao(client.id);
        if (!cancelled && data?.success && data.marcacao) {
          setSeed({
            [resolvedMarcacaoId]: normalizeSeedEntry(data.marcacao as MarcacaoSeed),
          });
          return;
        }
      } catch {
        /* fallback to client payload */
      }
      if (!cancelled) setSeed(fallbackSeed);
    })();

    return () => {
      cancelled = true;
    };
  }, [client, resolvedMarcacaoId]);

  if (!resolvedMarcacaoId) {
    return (
      <div className="rec-solicitar-proposta-overlay" role="presentation">
        <div className="rec-solicitar-proposta-loading">
          <p>Marcação inválida — não foi possível abrir Submeter Contrato.</p>
          <button type="button" className="rec-btn rec-btn-ghost" onClick={onClose}>
            Fechar
          </button>
        </div>
      </div>
    );
  }

  if (!seed) {
    return (
      <div className="rec-solicitar-proposta-overlay" role="presentation">
        <div className="rec-solicitar-proposta-loading">A carregar formulário…</div>
      </div>
    );
  }

  return (
    <div
      className="rec-solicitar-proposta-overlay"
      role="presentation"
      onMouseDown={swallowOverlayBackdropPointer}
    >
      <ComercialManagement
        embeddedContractOnly
        embeddedContractMarcacaoId={resolvedMarcacaoId}
        embeddedContractApi={embeddedContractApi}
        embeddedMarcacaoSeed={seed as Record<string, never>}
        onEmbeddedContractClose={onClose}
        onEmbeddedContractSuccess={onSuccess}
      />
    </div>
  );
};

export default CccSubmeterContratoRetencaoClientOverlay;
